// Reads and writes for the planning area, all through the generated client
// (spec 03 §5 rows 1 to 11). Every queryFn passes its signal; every write
// carries a fresh Idempotency-Key.

import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import type { components } from "@assessly/api-client";
import { client } from "../../../../lib/api";
import { refreshQuery, shouldRetryRead } from "../../../../lib/query";
import { RequestError, newIdempotencyKey, unwrap } from "../../../../lib/request";

type S = components["schemas"];
export type PlanningSettings = S["PlanningSettings"];
export type PlanningSettingsPut = S["PlanningSettingsPutRequest"];
export type PlanningParameters = S["PlanningParameters"];
export type PlanningParametersRow = S["PlanningParametersListItem"];
export type PlanningParametersPut = S["PlanningParametersPutRequest"];
export type SafetyStockPolicy = S["SafetyStockPolicy"];
export type SafetyStockPut = S["SafetyStockPolicyPutRequest"];
export type Forecast = S["Forecast"];
export type ForecastDetail = S["ForecastDetail"];
export type ForecastLine = S["ForecastLine"];
export type ForecastCreate = S["ForecastCreateRequest"];
export type ForecastPatch = S["ForecastPatchRequest"];
export type ForecastLinesPut = S["ForecastLinesPutRequest"];
export type Facility = S["Facility"];
export type Product = S["Product"];

/** The most rows one lines PUT may carry (contracts `FORECAST_LINES_MAX`). */
export const FORECAST_LINES_MAX = 2000;
/** The widest window a forecast read may name (contracts `FORECAST_WINDOW_MAX_WEEKS`). */
export const FORECAST_WINDOW_MAX_WEEKS = 104;
/** The longest note a forecast line may carry (contracts `FORECAST_LINE_NOTE_MAX`). */
export const FORECAST_LINE_NOTE_MAX = 500;
/** The horizon a facility uses before its settings answer (spec 03 §2.22 default). */
export const DEFAULT_HORIZON_WEEKS = 12;

const REF = { staleTime: 5 * 60_000, gcTime: 30 * 60_000, retry: false } as const;

export const planningKeys = {
  all: ["planning"] as const,
  facilities: () => ["planning", "facilities"] as const,
  settings: (facilityId: string) => ["planning", "settings", facilityId] as const,
  parameters: (facilityId: string) => ["planning", "parameters", facilityId] as const,
  parametersPage: (facilityId: string, filters: ParameterFilters, cursor: string | undefined) =>
    ["planning", "parameters", facilityId, filters, cursor ?? null] as const,
  parameterOf: (facilityId: string, productId: string) =>
    ["planning", "parameters", facilityId, "product", productId] as const,
  forecasts: (facilityId: string) => ["planning", "forecasts", facilityId] as const,
  forecastsPage: (facilityId: string, status: string | undefined, cursor: string | undefined) =>
    ["planning", "forecasts", facilityId, status ?? "all", cursor ?? null] as const,
  forecast: (id: string) => ["planning", "forecast", id] as const,
  forecastWindow: (id: string, from: string, to: string) =>
    ["planning", "forecast", id, from, to] as const,
  product: (id: string) => ["planning", "ref", "product", id] as const,
  productSearch: (q: string) => ["planning", "ref", "product-search", q] as const,
  uoms: () => ["planning", "ref", "uoms"] as const,
};

/** A field the problem body carries beside the RFC 9457 members. */
export function problemExtra(error: unknown, key: string): unknown {
  if (!(error instanceof RequestError) || error.problem === undefined) return undefined;
  return (error.problem as Record<string, unknown>)[key];
}

export function problemCode(error: unknown): string | undefined {
  return error instanceof RequestError ? error.code : undefined;
}

// ---- reference reads ---------------------------------------------------------

/** The facilities the reader's scope reaches; the service filters them (spec 16 §2.8). */
export function usePlanningFacilities(enabled = true) {
  return useQuery({
    ...REF,
    enabled,
    queryKey: planningKeys.facilities(),
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/org/facilities", { signal, params: { query: { status: "active" } } }),
        "facilities",
      ),
  });
}

/** Product id to its card, one cached read per product. */
export function useProductIndex(ids: readonly string[]) {
  const unique = Array.from(new Set(ids.filter((id) => id.length > 0))).sort();
  return useQueries({
    queries: unique.map((id) => ({
      ...REF,
      queryKey: planningKeys.product(id),
      queryFn: async ({ signal }: { signal: AbortSignal }) =>
        unwrap(
          await client.GET("/v1/catalogue/products/{id}", { signal, params: { path: { id } } }),
          "product",
        ),
    })),
    combine: (results) => {
      const byId = new Map<string, Product>();
      for (const result of results)
        if (result.data !== undefined) byId.set(result.data.id, result.data);
      return { byId, isPending: results.some((r) => r.isPending) };
    },
  });
}

// A plannable product is active or draft; a discontinued one is not (D-357 ruling 1).
const PLANNABLE = ["active", "draft"] as const;

/** Active then draft products whose SKU or name matches `q`, for the add-product typeahead. */
export function useProductSearch(q: string) {
  return useQuery({
    ...REF,
    queryKey: planningKeys.productSearch(q),
    enabled: q.length > 0,
    queryFn: async ({ signal }) => {
      const pages = await Promise.all(
        PLANNABLE.map(async (status) =>
          unwrap(
            await client.GET("/v1/catalogue/products", {
              signal,
              params: { query: { q, status, limit: 20 } },
            }),
            "products",
          ),
        ),
      );
      return { items: pages.flatMap((page) => page.items).slice(0, 20) };
    },
  });
}

export function useUoms() {
  return useQuery({
    ...REF,
    queryKey: planningKeys.uoms(),
    queryFn: async ({ signal }) =>
      unwrap(await client.GET("/v1/catalogue/uoms", { signal }), "uoms"),
  });
}

/** The most distinct product keys one paste resolves (spec 03 §4.3). */
export const PASTE_KEY_LIMIT = 200;

export interface ResolvedProductKeys {
  /** Lower-cased key to its product. */
  readonly found: Map<string, Product>;
  /** Lower-cased keys past the first 200, never looked up. */
  readonly overLimit: Set<string>;
  /** Lower-cased keys whose lookup failed, so whether they exist is unknown. */
  readonly failed: Set<string>;
}

/**
 * Resolves pasted product keys to products: active first, then draft, six keys in
 * flight, matching a SKU or a name exactly with case ignored (spec 03 §4.3).
 */
export async function resolveProductKeys(keys: readonly string[]): Promise<ResolvedProductKeys> {
  const byLower = new Map<string, string>();
  for (const k of keys) {
    const key = k.trim();
    const lower = key.toLocaleLowerCase();
    if (key.length > 0 && !byLower.has(lower)) byLower.set(lower, key);
  }
  const all = [...byLower.entries()];
  const distinct = all.slice(0, PASTE_KEY_LIMIT);
  const overLimit = new Set(all.slice(PASTE_KEY_LIMIT).map(([lower]) => lower));
  const found = new Map<string, Product>();
  const failed = new Set<string>();
  let next = 0;
  const lookup = async (lower: string, key: string): Promise<void> => {
    for (const status of PLANNABLE) {
      const res = await client.GET("/v1/catalogue/products", {
        params: { query: { q: key, status, limit: 50 } },
      });
      if (res.data === undefined) {
        failed.add(lower);
        return;
      }
      const hit = res.data.items.find(
        (p) => p.sku.toLocaleLowerCase() === lower || p.name.toLocaleLowerCase() === lower,
      );
      if (hit !== undefined) {
        found.set(lower, hit);
        return;
      }
    }
  };
  const worker = async () => {
    while (next < distinct.length) {
      const entry = distinct[next];
      next += 1;
      if (entry === undefined) continue;
      try {
        await lookup(entry[0], entry[1]);
      } catch {
        failed.add(entry[0]);
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(6, distinct.length) }, worker));
  return { found, overLimit, failed };
}

// ---- §2.22 settings ----------------------------------------------------------

export function usePlanningSettings(facilityId: string) {
  return useQuery({
    queryKey: planningKeys.settings(facilityId),
    enabled: facilityId.length > 0,
    retry: shouldRetryRead,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/planning/settings", {
          signal,
          params: { query: { facility_id: facilityId } },
        }),
        "planning settings",
      ),
  });
}

export async function putPlanningSettings(facilityId: string, body: PlanningSettingsPut) {
  return unwrap(
    await client.PUT("/v1/planning/settings", {
      params: {
        query: { facility_id: facilityId },
        header: { "Idempotency-Key": newIdempotencyKey() },
      },
      body,
    }),
    "planning settings",
  );
}

// ---- §2.1 parameters and §2.2 safety stock -----------------------------------

export interface ParameterFilters {
  readonly status: "active" | "archived" | "all";
  readonly replenishment_type?: "make" | "buy" | "transfer" | undefined;
  readonly mps_relevant?: "true" | "false" | undefined;
}

export function usePlanningParameters(
  facilityId: string,
  filters: ParameterFilters,
  cursor: string | undefined,
) {
  return useQuery({
    queryKey: planningKeys.parametersPage(facilityId, filters, cursor),
    enabled: facilityId.length > 0,
    retry: shouldRetryRead,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/planning/parameters", {
          signal,
          params: {
            query: {
              facility_id: facilityId,
              status: filters.status,
              ...(filters.replenishment_type !== undefined
                ? { replenishment_type: filters.replenishment_type }
                : {}),
              ...(filters.mps_relevant !== undefined ? { mps_relevant: filters.mps_relevant } : {}),
              ...(cursor !== undefined ? { cursor } : {}),
              limit: 50,
            },
          },
        }),
        "planning parameters",
      ),
  });
}

/** One product's row at the facility, archived or not, or null when it has none. */
export async function readParametersOf(
  facilityId: string,
  productId: string,
): Promise<PlanningParametersRow | null> {
  const page = unwrap(
    await client.GET("/v1/planning/parameters", {
      params: {
        query: { facility_id: facilityId, product_id: productId, status: "all", limit: 1 },
      },
    }),
    "planning parameters",
  );
  return page.items[0] ?? null;
}

export async function putPlanningParameters(
  facilityId: string,
  productId: string,
  body: PlanningParametersPut,
) {
  return unwrap(
    await client.PUT("/v1/planning/parameters/{productId}", {
      params: {
        path: { productId },
        query: { facility_id: facilityId },
        header: { "Idempotency-Key": newIdempotencyKey() },
      },
      body,
    }),
    "planning parameters",
  );
}

export async function putSafetyStock(facilityId: string, productId: string, body: SafetyStockPut) {
  return unwrap(
    await client.PUT("/v1/planning/safety-stock-policies/{productId}", {
      params: {
        path: { productId },
        query: { facility_id: facilityId },
        header: { "Idempotency-Key": newIdempotencyKey() },
      },
      body,
    }),
    "safety stock",
  ) as SafetyStockPolicy | null;
}

// ---- §2.3 and §2.4 forecasts -------------------------------------------------

export function useForecasts(
  facilityId: string,
  status: Forecast["status"] | undefined,
  cursor: string | undefined,
) {
  return useQuery({
    queryKey: planningKeys.forecastsPage(facilityId, status, cursor),
    enabled: facilityId.length > 0,
    retry: shouldRetryRead,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/forecasts", {
          signal,
          params: {
            query: {
              facility_id: facilityId,
              ...(status !== undefined ? { status } : {}),
              ...(cursor !== undefined ? { cursor } : {}),
              limit: 50,
            },
          },
        }),
        "forecasts",
      ),
  });
}

/**
 * The header and the lines of one window. Without bounds the server answers
 * its default window, this week across the facility's horizon (spec 03 §5).
 */
export function useForecastWindow(id: string, window: { from: string; to: string } | undefined) {
  return useQuery({
    queryKey: planningKeys.forecastWindow(id, window?.from ?? "default", window?.to ?? "default"),
    enabled: id.length > 0,
    retry: shouldRetryRead,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/forecasts/{id}", {
          signal,
          params: {
            path: { id },
            query: window !== undefined ? { bucket_from: window.from, bucket_to: window.to } : {},
          },
        }),
        "forecast",
      ),
  });
}

export async function createForecast(body: ForecastCreate) {
  return unwrap(
    await client.POST("/v1/forecasts", {
      params: { header: { "Idempotency-Key": newIdempotencyKey() } },
      body,
    }),
    "forecast",
  );
}

export async function patchForecast(id: string, body: ForecastPatch) {
  return unwrap(
    await client.PATCH("/v1/forecasts/{id}", {
      params: { path: { id }, header: { "Idempotency-Key": newIdempotencyKey() } },
      body,
    }),
    "forecast",
  );
}

export async function putForecastLines(id: string, body: ForecastLinesPut) {
  return unwrap(
    await client.PUT("/v1/forecasts/{id}/lines", {
      params: { path: { id }, header: { "Idempotency-Key": newIdempotencyKey() } },
      body,
    }),
    "forecast lines",
  );
}

/** Refetches every planning read of a facility after a write. */
export function usePlanningRefresh() {
  const qc = useQueryClient();
  return {
    settings: (facilityId: string) => refreshQuery(qc, planningKeys.settings(facilityId)),
    parameters: (facilityId: string) => refreshQuery(qc, planningKeys.parameters(facilityId)),
    forecasts: (facilityId: string) => refreshQuery(qc, planningKeys.forecasts(facilityId)),
    forecast: (id: string) => refreshQuery(qc, planningKeys.forecast(id)),
  };
}
